import {Injectable} from '@angular/core';

import {
    Router,
    ActivatedRouteSnapshot,
    RouterStateSnapshot,
    CanLoad,
    CanActivate,
    CanActivateChild,
    Route,
    UrlSegment
} from '@angular/router';

import {Observable} from 'rxjs';
import {map} from 'rxjs/operators';

import * as moment from 'moment';

import {ServerResponseData, HttpStatusCodesEnum, AuthApiService} from '@spa/api';

import {CacheCollectionService} from './cache-collection.service';

import {Helper} from '../classes';
import {PermissionRuleInterface} from '../interfaces';
import {PermissionRuleCheckStrategiesEnum, PermissionsEnum} from '../enums';

import {ErrorMessageIdSelector} from '../../../assets/descriptors';
import {CommonMessages} from '../../../assets/messages/common.messages';

@Injectable({
    providedIn: 'root'
})
export class AccessGuardService implements CanActivate, CanActivateChild, CanLoad {
    constructor(
        private authenticationService: AuthApiService,
        protected router: Router,
        protected cacheCollectionService: CacheCollectionService
    ) {
    }

    private navigateToErrorPage(message: string): void {
        const timestamp = moment().unix();

        this.cacheCollectionService.addItem(timestamp.toString(), message);

        this.router.navigate(['/error'], {
            queryParams: {
                [ErrorMessageIdSelector]: timestamp
            }
        }).finally();
    }

    private isValidRoutePermission(permissionRule: any): permissionRule is PermissionRuleInterface {
        const checkPermissionRuleStrategies = [
            PermissionRuleCheckStrategiesEnum.Union,
            PermissionRuleCheckStrategiesEnum.Intersect
        ];

        return (
            permissionRule &&
            permissionRule.permissions &&
            Array.isArray(permissionRule.permissions) &&
            (typeof permissionRule.strategy === 'number') &&
            (checkPermissionRuleStrategies.indexOf(permissionRule.strategy) >= 0)
        );
    }

    private doCheck(route: Route | ActivatedRouteSnapshot): any {
        return this.authenticationService.getAuthenticatedUserAllPermissionsAsync().pipe(
            map((response: ServerResponseData<boolean>) => {
                if (response.status !== HttpStatusCodesEnum.Ok) {
                    let message = CommonMessages.UnknownError.message;

                    switch (response.status) {
                        case HttpStatusCodesEnum.Unauthorized:
                            message = CommonMessages.UnauthorizedError.message;
                            break;
                        case HttpStatusCodesEnum.Forbidden:
                            message = CommonMessages.ForbiddenError.message;
                            break;
                        case HttpStatusCodesEnum.InternalServerError:
                            message = CommonMessages.InternalServerError.message;
                            break;
                    }

                    this.navigateToErrorPage(message);

                    return false;
                }

                const authUser = this.authenticationService.getAuthenticatedUser();

                if (authUser.permissions.indexOf(PermissionsEnum.CrmAllAll) < 0) {
                    if (route.data && route.data.hasOwnProperty('permissionRule')) {
                        if (!this.isValidRoutePermission(route.data.permissionRule)) {
                            this.navigateToErrorPage(CommonMessages.InvalidRoutePermission.message);

                            return false;
                        }

                        let permissionCheck = false;

                        const permissionRules = route.data.permissionRule as PermissionRuleInterface;

                        switch (permissionRules.strategy) {
                            case PermissionRuleCheckStrategiesEnum.Intersect:
                                permissionCheck = Helper.getIntersectStrategyPermissionsCheck(
                                    permissionRules.permissions,
                                    authUser.permissions
                                );
                                break;
                            case PermissionRuleCheckStrategiesEnum.Union:
                                permissionCheck = Helper.getUnionStrategyPermissionsCheck(
                                    permissionRules.permissions,
                                    authUser.permissions
                                );
                                break;
                        }

                        if (!permissionCheck) {

                            this.navigateToErrorPage(CommonMessages.ForbiddenError.message);

                            return false;
                        }
                    }
                }

                return true;
            })
        );
    }

    canLoad(route: Route, segments: UrlSegment[]): Observable<boolean> {
        return this.doCheck(route);
    }

    canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> {
        return this.doCheck(route);
    }

    canActivateChild(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> {
        return this.doCheck(route);
    }
}
